import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, RotateCcw, AlertCircle, DollarSign, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../ui/Button";
import { Withdrawal } from "../../types/payment";

interface RetryWithdrawalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  withdrawal: Withdrawal | null;
  onRetry: (withdrawal: Withdrawal) => Promise<void>;
}

const RetryWithdrawalDialog: React.FC<RetryWithdrawalDialogProps> = ({
  isOpen,
  onClose,
  withdrawal,
  onRetry,
}) => {
  const [retrying, setRetrying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!withdrawal || !isOpen) return null;

  const handleRetry = async () => {
    try {
      setRetrying(true);
      setError(null);
      await onRetry(withdrawal);
      setSuccess(true);
      
      // Close after showing success
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to retry withdrawal. Please try again.");
      setRetrying(false);
    }
  };

  const handleClose = () => {
    if (!retrying) {
      setSuccess(false);
      setError(null);
      onClose();
    }
  };

  const formatCurrency = (amount: number, currency?: string | null) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "USD").toUpperCase()
    }).format(amount);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl max-w-md w-full shadow-2xl"
          >
            {success ? (
              // Success State
              <div className="p-6 text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Retry Submitted!
                </h3>
                <p className="text-gray-600">
                  Your new withdrawal request has been created successfully.
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-6 border-b">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <RotateCcw className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Retry Withdrawal</h2>
                        <p className="text-sm text-gray-600">Create a new withdrawal request</p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      disabled={retrying}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <X className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  {/* Warning Message */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-yellow-800">
                          Retry Failed Withdrawal
                        </h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          This will create a new withdrawal request with the same details.
                          The failed withdrawal will remain in your history for reference.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Withdrawal Details */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Withdrawal Details</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-semibold text-red-600">
                          -{formatCurrency(withdrawal.amount || 0, withdrawal.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Method:</span>
                        <span className="font-medium text-gray-900">
                          {withdrawal.withdrawalMethod === "paypal" 
                            ? "PayPal" 
                            : withdrawal.withdrawalMethod === "bank_account"
                              ? "Bank Account"
                              : "Stripe Connect"}
                        </span>
                      </div>
                      {withdrawal.paypalEmail && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">PayPal Email:</span>
                          <span className="font-medium text-gray-900">{withdrawal.paypalEmail}</span>
                        </div>
                      )}
                      {withdrawal.failureReason && (
                        <div className="pt-2 border-t">
                          <span className="text-gray-600 text-sm">Previous Failure:</span>
                          <p className="text-sm text-red-600 mt-1">{withdrawal.failureReason}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Error Message */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-red-50 border border-red-200 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Actions */}
                <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={retrying}
                    className="flex-1 sm:flex-none"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {retrying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retry Withdrawal
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RetryWithdrawalDialog;




